import { useAuth0 } from '@auth0/auth0-react';
import { WidgetOptions } from 'common/contexts/useWidgetOptionsContext';
import { NotificationType } from 'common/models/notification.model';
import { UserDataType } from 'common/models/user.model';
import { WidgetOptionsWithKey } from 'common/models/widget-config.model';
import { WidgetID } from 'common/models/widget.model';
import { RootState } from 'common/store';
import {
  useCreateUserDataRowMutation,
  useDeleteUserDataRowMutation,
  useGetUserDataListQuery,
  UserDataTypeResult,
  useUpdateUserDataRowMutation,
} from 'common/store/api/user-data.api';
import { showToast } from 'common/store/slices/notification.slice';
import {
  addWidgetOptions,
  selectAllWidgetOptionKeys,
  setWidgetOptions,
  updateWidgetOption,
} from 'common/store/slices/widget-config.slice';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

export const useGetWidgetOptions = () => {
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const { isAuthenticated } = useAuth0();
  const skipFetch = !isAuthenticated || !projectId;
  const { isLoading, isFetching, data, isUninitialized } = useGetUserDataListQuery(
    { type: UserDataType.WidgetOptions, projectId: projectId ?? '' },
    {
      skip: skipFetch,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  );

  useEffect(() => {
    if (!skipFetch && !isLoading && !isFetching) {
      dispatch(
        setWidgetOptions({ projectId, widgetOptions: (data as WidgetOptionsWithKey[]) ?? [] })
      );
    }
  }, [data, dispatch, isLoading, isFetching, projectId, skipFetch]);

  return { data, isLoading, isFetching, isUninitialized };
};

interface UsePersistWidgetOptionsReturn {
  saveOptions: (widgetOptions: WidgetOptions) => Promise<WidgetOptionsWithKey>;
  saveMultipleOptions: (options: Record<string, WidgetOptions>) => Promise<WidgetOptionsWithKey[]>;
  updateOptions: ({
    key,
    widgetOptions,
  }: {
    key: number;
    widgetOptions: WidgetOptions;
  }) => Promise<void>;
  deleteOptions: (key: number) => Promise<void>;
  deleteMultipleOptions: (keys: number[]) => Promise<any[]>;
  removeAllWidgetOptions: () => Promise<void>;
}

interface UsePersistWidgetOptionsProps {
  id: WidgetID;
}

function usePersistWidgetOptions(
  { id }: UsePersistWidgetOptionsProps = { id: '' }
): UsePersistWidgetOptionsReturn {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const [createWidgetOptions] = useCreateUserDataRowMutation();
  const [updateWidgetOptions] = useUpdateUserDataRowMutation();
  const [deleteWidgetOptions] = useDeleteUserDataRowMutation();
  const allWidgetOptionKeys = useSelector((state: RootState) =>
    selectAllWidgetOptionKeys(state, projectId ?? '')
  );

  const saveSingleOption = useCallback(
    async (widgetId: string, widgetOptions: WidgetOptions) =>
      new Promise<WidgetOptionsWithKey>((resolve, reject) => {
        if (!widgetId || widgetId === '') {
          throw new Error('Failed to save widget options: Widget ID is required');
        }

        const widgetOptionsWithId = { ...widgetOptions, id: widgetId };

        createWidgetOptions({
          userDataTypeId: UserDataType.WidgetOptions,
          data: widgetOptionsWithId as UserDataTypeResult,
        })
          .then((result: any) => {
            if (result?.data?.length > 0) {
              const key = result.data[0];

              dispatch(
                addWidgetOptions({
                  projectId: projectId ?? '',
                  widgetOptions: { key, value: widgetOptionsWithId },
                })
              );
              resolve({ key, value: widgetOptionsWithId });
            }
          })
          .catch((error) => {
            reject(error);
          });
      }),
    [createWidgetOptions, dispatch, projectId]
  );

  const saveOptions = useCallback(
    async (widgetOptions: WidgetOptions) => await saveSingleOption(id, widgetOptions),
    [saveSingleOption, id]
  );

  const saveMultipleOptions = useCallback(
    async (options: Record<string, WidgetOptions>) => {
      return await Promise.all(
        Object.entries(options).map(([id, value]) => {
          return saveSingleOption(id, value);
        })
      );
    },
    [saveSingleOption]
  );

  const updateOptions = useCallback(
    async ({ key, widgetOptions }: { key: number; widgetOptions: WidgetOptions }) => {
      const widgetOptionsWithId = { id, ...widgetOptions };

      const result: any = await updateWidgetOptions({
        userDataTypeId: UserDataType.WidgetOptions,
        key,
        data: widgetOptionsWithId as UserDataTypeResult,
      });

      if (result?.data?.length > 0) {
        dispatch(
          updateWidgetOption({
            projectId: projectId ?? '',
            widgetId: id,
            widgetOptionsWithKey: { key, value: widgetOptionsWithId },
          })
        );
      }
    },
    [updateWidgetOptions, id, projectId, dispatch]
  );

  const deleteSingleOption = useCallback(
    async (key: number) => await deleteWidgetOptions(key),
    [deleteWidgetOptions]
  );

  const deleteOptions = useCallback(
    async (key: number) => {
      await deleteSingleOption(key);
    },
    [deleteSingleOption]
  );

  const deleteMultipleOptions = useCallback(
    async (keys: number[]) => await Promise.all(keys.map((key) => deleteSingleOption(key))),
    [deleteSingleOption]
  );

  const removeAllWidgetOptions = useCallback(async () => {
    await Promise.all(allWidgetOptionKeys.map((key) => deleteSingleOption(key)))
      .then(() => {
        dispatch(
          showToast({
            id: 'widget-options-reset',
            type: NotificationType.SUCCESS,
            text: 'All widget options have been reset successfully.',
          })
        );
      })
      .catch(() => {
        dispatch(
          showToast({
            id: 'widget-options-reset-error',
            type: NotificationType.ERROR,
            text: 'An error occurred while resetting widget options.',
          })
        );
      });
  }, [allWidgetOptionKeys, deleteSingleOption, dispatch]);

  return {
    saveOptions,
    updateOptions,
    saveMultipleOptions,
    deleteOptions,
    deleteMultipleOptions,
    removeAllWidgetOptions,
  };
}

export default usePersistWidgetOptions;
